import React from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../design-system';

const Footer = () => {
  const navigate = useNavigate();

  const linkStyle = {
    color: 'var(--color-text-muted)',
    textDecoration: 'none',
    fontSize: '14px',
    lineHeight: '1.8',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'block',
    transition: 'color 0.15s ease',
  };

  const headingStyle = {
    color: 'var(--color-text)',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: '16px',
  };

  return (
    <footer style={{
      borderTop: `1px solid var(--color-border)`,
      backgroundColor: 'var(--color-surface)',
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '64px 24px 32px',
      }}>
        {/* Main grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '48px',
          marginBottom: '48px',
        }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <img src="/icons/cottage.png" alt="Cahoots" width="28" height="28" />
              <span style={{
                fontSize: '18px',
                fontWeight: '700',
                color: 'var(--color-text)',
                letterSpacing: '-0.3px',
              }}>
                Cahoots
              </span>
            </div>
            <p style={{
              color: 'var(--color-text-muted)',
              fontSize: '14px',
              lineHeight: '1.7',
              maxWidth: '320px',
              marginBottom: '24px',
            }}>
              Living documentation for what you're building. Start from an idea or your existing codebase — Cahoots keeps your specs in sync.
            </p>
            {/* Social links */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <a
                href="https://www.linkedin.com/company/cahoots-llc/about"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                style={{ color: 'var(--color-text-muted)', transition: 'color 0.15s ease' }}
                onMouseEnter={e => e.currentTarget.style.color = tokens.colors.primary[500]}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href="https://github.com/cahoots-org"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                style={{ color: 'var(--color-text-muted)', transition: 'color 0.15s ease' }}
                onMouseEnter={e => e.currentTarget.style.color = tokens.colors.primary[500]}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Product links */}
          <div>
            <p style={headingStyle}>Product</p>
            {[
              { label: 'Pricing', path: '/pricing' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact', path: '/contact' },
              { label: 'Get Started', path: '/login' },
            ].map(({ label, path }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                style={linkStyle}
                onMouseEnter={e => e.currentTarget.style.color = tokens.colors.primary[500]}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Legal links */}
          <div>
            <p style={headingStyle}>Legal</p>
            {[
              { label: 'Privacy Policy', path: '/privacy' },
              { label: 'Terms of Service', path: '/terms' },
            ].map(({ label, path }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                style={linkStyle}
                onMouseEnter={e => e.currentTarget.style.color = tokens.colors.primary[500]}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
              >
                {label}
              </button>
            ))}
            <a
              href="mailto:admin@cahoots.cc"
              style={{ ...linkStyle, display: 'block' }}
              onMouseEnter={e => e.currentTarget.style.color = tokens.colors.primary[500]}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
            >
              admin@cahoots.cc
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          borderTop: `1px solid var(--color-border)`,
          paddingTop: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '13px', margin: 0 }}>
            © {new Date().getFullYear()} Cahoots LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
